
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { 
  UserPlusIcon, 
  KeyIcon,
  DocumentTextIcon, 
  Cog6ToothIcon 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import useAdminDashboard from "./useAdminDashboard.js";
import CreateUserModal from '../../../Components/CreateUserModal.jsx';
import KeyGenerateModalAdmin from '../../../Components/KeyGenerateModalAdmin.jsx';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels,
  
);

export default function AdminDashboard() {
  const { loading, dashboardData ,
    showCreateUserModal,
    setShowCreateUserModal,
    onCloseModal,
    showApiKeyModal,
    setShowApiKeyModal
  } = useAdminDashboard();
  const navigate = useNavigate();
const windowWidth = window.innerWidth;
  // Map API data (with safe fallbacks)
  const statsData = {
    totalUsers: dashboardData?.totalUsers ?? 0,
    activeSubscriptions: dashboardData?.activeSubscriptions ?? 0,
    apiUsage: dashboardData?.totalApiUsage ?? 0,
  };

  // Dummy Data for Recent Activities
  const recentActivities = [
    { id: 1, title: "New user registered: john.doe@email.com", time: "2 minutes ago" },
    { id: 2, title: "API key generated for user: sarah.wilson", time: "15 minutes ago" },
    { id: 3, title: "Subscription upgraded: Premium plan", time: "1 hour ago" },
    { id: 4, title: "System maintenance completed", time: "2 hours ago" },
    { id: 5, title: "New weather data updated", time: "3 hours ago" },
    { id: 6, title: "User login: admin@weather.com", time: "4 hours ago" },
  ];

  // API Usage Chart mapping from response
  const apiUsageChartData = (() => {
    const points = dashboardData?.apiUsageChartData || [];
    const labels = points.map((p) => new Date(p.date).toLocaleDateString());
    const values = points.map((p) => p.usage ?? 0);
    return {
      labels: labels.length ? labels : ["-"],
      datasets: [
        {
          label: 'API Calls',
          data: values.length ? values : [0],
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  })();

  const apiUsageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#9CA3AF',
        },
      },
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#9CA3AF',
        },
      },
    },
  };

  // Subscription chart mapping from response
  const subscriptionChartData = (() => {
    const items = dashboardData?.subscriptionChartData || [];
    const labels = items.map((i) => i.planName || i.type || 'Plan');
    const values = items.map((i) => i.count ?? 0);
    const palette = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
    const colors = values.map((_, idx) => palette[idx % palette.length]);
    return {
      labels: labels.length ? labels : ["-"],
      datasets: [
        {
          label: 'Subscriptions',
          data: values.length ? values : [0],
          backgroundColor: colors,
          borderWidth: 0,
        },
      ],
    };
  })();

  const subscriptionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#fff',
          padding: 20,
          usePointStyle: true,
          font: {
            size: 13
        },
          
        },
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 14
      },
        formatter: (value, context) => {
          const dataset = context.chart.data.datasets[0];
          const total = dataset.data.reduce((sum, v) => sum + v, 0);
          const pct = total ? (value / total) * 100 : 0;
          return pct >= 5 ? `${pct.toFixed(0)}%` : '';
        },
      },
    },
  };

  return (
    <>
    <div className="p-6 space-y-8">
      {/* First Row: 3 Stats Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Users Box */}
        <div className="bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-white">Total Users</h1>
            {/* <div className="w-3 h-3 bg-green-500 rounded-full"></div> */}
          </div>
          <div className="text-6xl text-right font-bold text-primaryBlue">
            {statsData?.totalUsers?.toLocaleString()||0}
          </div>
        </div>

        {/* Active Subscriptions Box */}
        <div className="bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-white">Active Subscriptions</h1>
            {/* <div className="w-3 h-3 bg-blue-500 rounded-full"></div> */}
          </div>
          <div className="text-6xl text-right font-bold text-primaryBlue">
            {statsData?.activeSubscriptions?.toLocaleString()||0}
          </div>
        </div>

        {/* API Usage Box */}
        <div className="bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-white">API Usage</h1>
            {/* <div className="w-3 h-3 bg-yellow-500 rounded-full"></div> */}
          </div>
          <div className="text-6xl text-right font-bold text-primaryBlue">
            {statsData?.apiUsage?.toLocaleString()||0}
          </div>
            </div>
          </div>

      {/* Second Row: 2 Chart Boxes */}
      <div className="grid grid-cols-3 lg:grid-cols-5 gap-6">
  {/* API Usage Chart Box */}
  <div className="col-span-3 bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
    <h3 className="text-2xl font-semibold text-white mb-6">API Usage</h3>
    <div className="h-52">
      <Line data={apiUsageChartData} options={apiUsageChartOptions} />
    </div>
  </div>

  {/* Subscription Plans Chart Box */}
  <div className="col-span-3 md:col-span-2 bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
    <h3 className="text-2xl font-semibold text-white mb-6">Subscription Plans</h3>
    <div className="h-52">
      <Pie style={{marginLeft: windowWidth > 768 ? '-4rem' : '0px'}} data={subscriptionChartData} options={subscriptionChartOptions} />
    </div>
  </div>
</div>

      {/* Third Row: Recent Activities & Action Buttons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities Box */}
        <div className="bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
          <h3 className="text-2xl font-semibold text-white mb-6">Recent Activities</h3>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {(dashboardData?.recentActivities || []).map((activity) => {
              const title = activity?.details?.message || activity?.action || 'Activity';
              const time = activity?.timestamp
                ? new Date(activity.timestamp).toLocaleString()
                : '';
              return (
                <div key={activity.id} className="flex justify-between items-center py-2 border-b border-borderGray last:border-b-0">
                  <span className="text-white font-medium text-sm flex-1 pr-4">{title}</span>
                  <span className="text-gray-400 text-xs whitespace-nowrap">{time}</span>
                </div>
              );
            })}
            {(!dashboardData?.recentActivities || dashboardData.recentActivities.length === 0) && (
              <div className="text-gray-400 text-sm">No recent activities</div>
            )}
          </div>
        </div>

        {/* Action Buttons Box */}
        <div className="bg-blueGray border-2 border-borderGray rounded-lg p-6 shadow-sm">
          <h3 className="text-2xl font-semibold text-white mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            {/* Create New User Button */}
            <button 
              onClick={() => setShowCreateUserModal(true)}
              className="flex items-center justify-center py-2 p-4 bg-transparent hover:bg-white/10 rounded-md transition-colors duration-200 group border-2 border-white hover:border-primaryBlue"
            >
              <UserPlusIcon className="h-6 w-6 text-primaryBlue mr-3 group-hover:scale-110 transition-transform" />
              <span className="text-white text-md font-medium">Create New User</span>
            </button>

            {/* Generate API Key Button */}
            <button onClick={()=>setShowApiKeyModal(true)} className="flex items-center justify-center py-2 p-4 bg-transparent hover:bg-white/10 rounded-md transition-colors duration-200 group border-2 border-white hover:border-green-500">
              <KeyIcon className="h-6 w-6 text-green-500 mr-3 group-hover:scale-110 transition-transform" />
              <span className="text-white text-md font-medium">Generate API Key</span>
            </button>

            {/* View API Documentation Button */}
            <button className="flex items-center justify-center py-2 p-4 bg-transparent hover:bg-white/10 rounded-md transition-colors duration-200 group border-2 border-white hover:border-purple-500">
              <DocumentTextIcon className="h-6 w-6 text-purple-500 mr-3 group-hover:scale-110 transition-transform" />
              <span className="text-white text-md font-medium">View API Docs</span>
          </button>

            {/* Manage Subscriptions Button */}
            <button onClick={()=>{navigate("/Admin/Dashboard/Subscription")}} className="flex items-center justify-center py-2 p-4 bg-transparent hover:bg-white/10 rounded-md transition-colors duration-200 group border-2 border-white hover:border-orange-500">
              <Cog6ToothIcon className="h-6 w-6 text-orange-500 mr-3 group-hover:scale-110 transition-transform" />
              <span className="text-white text-md font-medium">Manage Subscription</span>
          </button>
          </div>
        </div>
      </div>
    </div>
    <CreateUserModal open={showCreateUserModal} onClose={()=>setShowCreateUserModal(false)} />
    <KeyGenerateModalAdmin open={showApiKeyModal} onClose={()=>setShowApiKeyModal(false)} />
    </>

  );
}
